import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import ArticleCard from '@/components/feature/ArticleCard';
import { useSeo } from '@/hooks/useSeo';
import { allSections, formatViews, getArticlesBySection, getSection } from '@/utils/content';

export default function SectionPage() {
  const { slug } = useParams<{ slug: string }>();
  const section = getSection(slug);
  const [activeTag, setActiveTag] = useState('全部');

  const articles = useMemo(() => (slug ? getArticlesBySection(slug) : []), [slug]);

  const tags = useMemo(() => {
    const tagSet = new Set<string>();
    articles.forEach((article) => article.tags.forEach((tag) => tagSet.add(tag)));
    return ['全部', ...Array.from(tagSet)];
  }, [articles]);

  const visible = activeTag === '全部' ? articles : articles.filter((article) => article.tags.includes(activeTag));
  const hotInSection = articles.slice().sort((a, b) => b.views - a.views).slice(0, 4);
  const origin = typeof window !== 'undefined' ? window.location.origin : '';

  const jsonLd = useMemo(() => {
    if (!section) {
      return undefined;
    }
    return [
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: `${section.name} - ${section.tagline}`,
        description: section.description,
        url: `${origin}/section/${section.slug}`,
        inLanguage: 'zh-CN',
        isPartOf: { '@type': 'WebSite', name: '17吃瓜网', url: `${origin}/` },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: `${origin}/` },
          { '@type': 'ListItem', position: 2, name: section.name, item: `${origin}/section/${section.slug}` },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: `${section.name}内容列表`,
        itemListElement: articles.map((article, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: article.title,
          url: `${origin}/article/${article.id}`,
        })),
      },
    ];
  }, [section, articles, origin]);

  useSeo({
    title: section ? `${section.name} - ${section.tagline}` : '板块不存在 - 17吃瓜网',
    description: section
      ? `${section.description} 收录 ${articles.length} 篇${section.name}整理内容。`
      : '该板块不存在，返回首页查看全部板块分区。',
    keywords: section ? `17吃瓜网,${section.name},${section.shortName},${section.tagline}` : '17吃瓜网,板块导航',
    path: section ? `/section/${section.slug}` : '/section',
    jsonLd,
  });

  if (!section) {
    return (
      <SiteLayout>
        <div className="mx-auto flex w-full flex-col items-center px-4 py-24 text-center md:px-6">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-500">
            <i className="ri-emotion-sad-line text-3xl" />
          </span>
          <h1 className="mt-6 font-heading text-2xl font-black text-foreground-950 md:text-3xl">这个分区还不存在</h1>
          <p className="mt-3 text-sm text-foreground-600">可能链接有误，回到首页看看现有的五个板块吧。</p>
          <Link
            to="/"
            className="mt-6 flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-home-4-line" /> 返回首页
          </Link>
        </div>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      <section className="w-full border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full px-4 py-8 md:px-6 md:py-10">
          <Breadcrumbs items={[{ label: '首页', to: '/' }, { label: section.name }]} />
          <div className="mt-6 flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-start gap-4">
              <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                <i className={`${section.icon} text-3xl`} />
              </span>
              <div>
                <h1 className="font-heading text-2xl font-black text-foreground-950 md:text-4xl">
                  {section.name}
                </h1>
                <p className="mt-1 text-sm font-medium text-primary-600">{section.tagline}</p>
                <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">{section.description}</p>
              </div>
            </div>
            <div className="flex gap-6 rounded-lg border border-background-200 bg-background-50 px-6 py-4">
              <div>
                <span className="block font-heading text-2xl font-black text-foreground-950">{articles.length}</span>
                <span className="text-xs text-foreground-500">整理篇数</span>
              </div>
              <div className="border-l border-background-200 pl-6">
                <span className="block font-heading text-2xl font-black text-foreground-950">
                  {articles.filter((article) => article.hot).length}
                </span>
                <span className="text-xs text-foreground-500">热门话题</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full bg-background-50">
        <div className="mx-auto grid w-full grid-cols-1 gap-8 px-4 py-10 md:px-6 md:py-12 lg:grid-cols-12 lg:gap-10">
          <div className="lg:col-span-8">
            <div className="flex flex-wrap items-center gap-2">
              {tags.map((tag) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => setActiveTag(tag)}
                  className={`cursor-pointer whitespace-nowrap rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors md:text-sm ${
                    activeTag === tag
                      ? 'border-primary-500 bg-primary-500 text-background-50'
                      : 'border-background-200 bg-background-100 text-foreground-600 hover:border-primary-300 hover:text-primary-600'
                  }`}
                >
                  {tag}
                </button>
              ))}
            </div>

            <div className="mt-6 flex flex-col gap-4">
              {visible.map((article) => (
                <ArticleCard key={article.id} article={article} variant="list" />
              ))}
            </div>

            {visible.length === 0 && (
              <div className="mt-6 rounded-lg border border-dashed border-background-300 bg-background-100 p-10 text-center">
                <p className="text-sm text-foreground-600">该标签下暂时没有内容，换个标签试试。</p>
              </div>
            )}
          </div>

          <aside className="lg:col-span-4">
            <div className="rounded-lg border border-background-200 bg-background-100 p-5">
              <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                <i className="ri-fire-line text-primary-500" /> 本区热门
              </h2>
              <ul className="mt-4 space-y-3">
                {hotInSection.map((article, index) => (
                  <li key={article.id}>
                    <Link to={`/article/${article.id}`} className="group flex cursor-pointer items-start gap-3">
                      <span
                        className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-xs font-bold ${
                          index === 0 ? 'bg-primary-500 text-background-50' : 'bg-background-200 text-foreground-700'
                        }`}
                      >
                        {index + 1}
                      </span>
                      <span>
                        <span className="line-clamp-2 block text-sm font-medium leading-snug text-foreground-800 transition-colors group-hover:text-primary-600">
                          {article.title}
                        </span>
                        <span className="mt-1 block text-xs text-foreground-500">{formatViews(article.views)} 阅读</span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-5 rounded-lg border border-background-200 bg-background-100 p-5">
              <h2 className="font-heading text-lg font-bold text-foreground-950">其他板块</h2>
              <ul className="mt-4 flex flex-wrap gap-2">
                {allSections
                  .filter((item) => item.slug !== section.slug)
                  .map((item) => (
                    <li key={item.slug}>
                      <Link
                        to={`/section/${item.slug}`}
                        className="flex cursor-pointer items-center gap-1.5 rounded-full border border-background-200 bg-background-50 px-3.5 py-2 text-xs font-medium text-foreground-700 transition-colors hover:border-secondary-400 hover:text-secondary-800"
                      >
                        <i className={item.icon} /> {item.name}
                      </Link>
                    </li>
                  ))}
              </ul>
            </div>
          </aside>
        </div>
      </section>
    </SiteLayout>
  );
}